import { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import type { Article } from '../../store/apis/helpCenter.api';
import { ArticleListSkeleton } from './Skeleton';
import { HelpArticleRow } from './HelpArticleRow';
import { SimpleTooltip } from '../common';
import { scrollToTop } from '../survey/hooks';

interface ArticleListProps {
  title: string;
  articles: Article[];
  loading: boolean;
  categoryNameForRows: (article: Article) => string;
  onBack: () => void;
  onArticleClick: (articleId: string) => void;
}

/** Figma open-category panel: header + article rows, expands in place (dc.html:1841). */
export function ArticleList({ title, articles, loading, categoryNameForRows, onBack, onArticleClick }: ArticleListProps) {
  useEffect(() => {
    scrollToTop();
  }, [title]);

  const countLabel = `${articles.length} ${articles.length === 1 ? 'article' : 'articles'}`;

  return (
    <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-card shadow-[var(--shadow-sm)] p-0 overflow-hidden">
      <div className="flex items-center justify-between px-[22px] py-[18px] border-b border-[var(--color-border-tertiary)]">
        <div className="flex items-center gap-2.5 min-w-0">
          <SimpleTooltip label="Back to all categories" side="top">
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to all categories"
            className="relative grid place-items-center w-8 h-8 rounded-button text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-muted)] hover:text-[var(--color-primary-700)] transition-colors cursor-pointer border-0 bg-transparent flex-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:outline-none after:absolute after:-inset-2 after:content-['']"
          >
            <ArrowLeft className="w-[17px] h-[17px]" />
          </button>
          </SimpleTooltip>
          <h3 className="text-heading-sm text-[var(--color-text-primary)] truncate">
            {title}
          </h3>
        </div>
        {!loading && (
          <span className="text-caption font-bold text-[var(--color-primary-700)] bg-[var(--color-primary-100)] px-[11px] py-1 rounded-full flex-none">
            {countLabel}
          </span>
        )}
      </div>

      {loading ? (
        <ArticleListSkeleton />
      ) : articles.length > 0 ? (
        <div>
          {articles.map((article) => (
            <HelpArticleRow
              key={article._id}
              article={article}
              categoryName={categoryNameForRows(article)}
              onOpen={onArticleClick}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 px-6">
          <h3 className="text-heading-sm text-[var(--color-text-primary)] mb-1">No articles yet</h3>
          <p className="text-body-xs text-[var(--color-text-secondary)] max-w-xs mx-auto">
            We're currently working on documentation for this module.
          </p>
        </div>
      )}
    </div>
  );
}

export default ArticleList;
